/**
 * Badge Collection Card
 * Grid showing all badges with earned/locked states - ported from mobile
 */

import {
  Scan,
  MapPin,
  UserPlus,
  Users,
  Award,
  CalendarCheck,
  Footprints,
  Search,
  Target,
  Star,
  Trophy,
  Crown,
  Lock,
} from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { Badge, EarnedBadge } from '../types';
import { formatBadgeDate } from '../utils/helpers';

interface BadgeCollectionCardProps {
  badges: Badge[];
  earnedBadges: EarnedBadge[];
}

// Map icon names to components
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  scan: Scan,
  'map-pin': MapPin,
  'user-plus': UserPlus,
  users: Users,
  award: Award,
  'calendar-check': CalendarCheck,
  footprints: Footprints,
  search: Search,
  target: Target,
  star: Star,
  trophy: Trophy,
  crown: Crown,
};

interface BadgeItemProps {
  badge: Badge;
  isEarned: boolean;
}

function BadgeItem({ badge, isEarned }: BadgeItemProps) {
  const Icon = iconMap[badge.icon] || Award;

  return (
    <div
      className={`relative p-4 rounded-xl transition-all ${
        isEarned
          ? 'bg-gradient-to-br from-primary-50 to-white border border-primary-100 shadow-sm'
          : 'bg-gray-50 border border-gray-100'
      }`}
    >
      {/* Badge Icon */}
      <div
        className={`w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center ${
          isEarned
            ? 'bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg'
            : 'bg-gray-200'
        }`}
      >
        {isEarned ? (
          <Icon className="w-7 h-7 text-white" />
        ) : (
          <div className="relative">
            <Icon className="w-7 h-7 text-gray-400" />
            <Lock className="w-4 h-4 text-gray-500 absolute -bottom-1 -right-1" />
          </div>
        )}
      </div>

      {/* Badge Info */}
      <div className="text-center">
        <h4
          className={`font-medium text-sm mb-1 ${
            isEarned ? 'text-gray-900' : 'text-gray-500'
          }`}
        >
          {badge.name}
        </h4>
        <p
          className={`text-xs ${
            isEarned ? 'text-gray-600' : 'text-gray-400'
          }`}
        >
          {badge.description}
        </p>
        {isEarned && badge.earnedAt && (
          <p className="text-xs text-primary-600 mt-2 font-medium">
            {formatBadgeDate(badge.earnedAt)}
          </p>
        )}
      </div>

      {/* Earned indicator */}
      {isEarned && (
        <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-md">
          <svg
            className="w-4 h-4 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
      )}
    </div>
  );
}

export function BadgeCollectionCard({ badges, earnedBadges }: BadgeCollectionCardProps) {
  const { t } = useLanguage();

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Award className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t('challenges.badges.title', 'Badge Collection')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('challenges.badges.subtitle', 'Collect all badges to become a master!')}
              </p>
            </div>
          </div>
          <div className="px-3 py-1 bg-primary-100 rounded-full">
            <span className="text-sm font-medium text-primary-700">
              {earnedBadges.length} / {badges.length}
            </span>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Progress bar */}
        <div className="mb-6">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-500"
              style={{ width: `${(earnedBadges.length / badges.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Badge Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {badges.map((badge) => (
            <BadgeItem
              key={badge.id}
              badge={badge}
              isEarned={badge.earnedAt != null}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
